import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChild } from '../../contexts/ChildContext';
import { useTimer } from '../../contexts/TimerContext';
import {
  quickLogDiaper,
  quickLogBottle,
  getLastBottleOz,
} from '../../utils/quickLogUtils';
import '../../styles/quickLogFAB.css';

/**
 * QuickLogFAB — mobile-only floating action button.
 *
 * A fixed + button (bottom-right) that opens a bottom sheet with two zones:
 *   1. Quick Log — 1-tap: wet nappy, dirty nappy, bottle feed (adjustable oz),
 *      start nap timer, start breastfeed timer.
 *   2. Navigate  — full section list, replacing the hidden mobile tab bar.
 *
 * Props:
 *   navSections  { id, label, icon, path, visible }[]  from AppLayout
 */
export default function QuickLogFAB({ navSections = [], onOpenFeedback }) {
  const { user, profile, logout }    = useAuth();
  const { selectedChild }           = useChild();
  const { timer, start: startTimer } = useTimer();
  const navigate                    = useNavigate();

  const [isOpen,   setIsOpen]   = useState(false);
  const [toast,    setToast]    = useState(null);
  const [logging,  setLogging]  = useState(false);
  const [bottleOz, setBottleOz] = useState(
    () => getLastBottleOz(selectedChild?.id)
  );

  // Add body class so the timer banner can hide itself while the sheet is open
  useEffect(() => {
    if (isOpen) document.body.classList.add('ql-sheet-open');
    else document.body.classList.remove('ql-sheet-open');
    return () => document.body.classList.remove('ql-sheet-open');
  }, [isOpen]);

  const close = () => setIsOpen(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const withLog = async (action, successMsg) => {
    if (!selectedChild || !user || logging) return;
    setLogging(true);
    try {
      await action();
      showToast(successMsg);
    } catch {
      showToast('Something went wrong — try the full section');
    } finally {
      setLogging(false);
    }
  };

  const handleDiaper = (type) =>
    withLog(
      () => quickLogDiaper(type, selectedChild.id, user, profile),
      type === 'wet' ? '💧 Wet nappy logged' : '💩 Dirty nappy logged'
    );

  const handleBottle = () =>
    withLog(
      () => quickLogBottle(bottleOz, selectedChild.id, user, profile),
      `🍼 ${bottleOz} oz logged`
    );

  const handleTimer = (type) => {
    startTimer(type);
    showToast(type === 'sleep' ? '😴 Nap timer started' : '🤱 Feed timer started');
    close();
  };

  const handleNav = (path) => {
    navigate(path);
    close();
  };

  const adjustOz = (delta) =>
    setBottleOz((prev) =>
      Math.max(0.5, parseFloat((prev + delta).toFixed(1)))
    );

  const visibleSections  = navSections.filter((s) => s.visible !== false);
  const sleepActive      = timer?.type === 'sleep';
  const breastfeedActive = timer?.type === 'breastfeeding';

  if (!selectedChild) return null;

  return (
    <>
      {/* Sheet backdrop */}
      {isOpen && (
        <div className="ql-backdrop" onClick={close} aria-hidden="true" />
      )}

      {/* Bottom sheet */}
      {isOpen && (
        <div className="ql-sheet" role="dialog" aria-label="Quick log and navigation">
          <div className="ql-handle" aria-hidden="true" />

          {toast && <div className="ql-toast" role="status">{toast}</div>}

          {/* ── Zone 1: Quick log ─────────────────────────────── */}
          <div className="ql-zone">
            <p className="ql-zone-label">⚡ Quick Log</p>

            <div className="ql-grid">
              <button
                className="ql-action-btn"
                onClick={() => handleDiaper('wet')}
                disabled={logging}
              >
                <span className="ql-action-icon">💧</span>
                Wet nappy
              </button>
              <button
                className="ql-action-btn"
                onClick={() => handleDiaper('dirty')}
                disabled={logging}
              >
                <span className="ql-action-icon">💩</span>
                Dirty nappy
              </button>
            </div>

            {/* Bottle with inline ±0.5 oz adjuster */}
            <div className="ql-bottle-row">
              <button
                className="ql-oz-btn"
                onClick={() => adjustOz(-0.5)}
                aria-label="Decrease amount by 0.5 oz"
              >−</button>
              <button
                className="ql-action-btn ql-bottle-btn"
                onClick={handleBottle}
                disabled={logging}
              >
                <span className="ql-action-icon">🍼</span>
                {bottleOz} oz bottle
              </button>
              <button
                className="ql-oz-btn"
                onClick={() => adjustOz(0.5)}
                aria-label="Increase amount by 0.5 oz"
              >+</button>
            </div>

            <div className="ql-grid">
              <button
                className={`ql-action-btn${sleepActive ? ' ql-active' : ''}`}
                onClick={() => !sleepActive && handleTimer('sleep')}
                disabled={sleepActive}
              >
                <span className="ql-action-icon">😴</span>
                {sleepActive ? 'Nap running…' : 'Start nap'}
              </button>
              <button
                className={`ql-action-btn${breastfeedActive ? ' ql-active' : ''}`}
                onClick={() => !breastfeedActive && handleTimer('breastfeeding')}
                disabled={breastfeedActive}
              >
                <span className="ql-action-icon">🤱</span>
                {breastfeedActive ? 'Feed running…' : 'Start feed'}
              </button>
            </div>
          </div>

          <div className="ql-divider" />

          {/* ── Zone 2: Navigate ──────────────────────────────── */}
          <div className="ql-zone ql-nav-zone">
            <p className="ql-zone-label">Navigate</p>
            <div className="ql-nav-list">
              {visibleSections.map((section) => (
                <button
                  key={section.id}
                  className="ql-nav-item"
                  onClick={() => handleNav(section.path)}
                >
                  <span className="ql-nav-icon" aria-hidden="true">{section.icon}</span>
                  {section.label}
                </button>
              ))}
            </div>

            <div className="ql-divider" style={{ margin: '0.75rem 0 0.5rem' }} />

            <button
              className="ql-nav-item"
              style={{ width: '100%' }}
              data-tutorial="settings-link-fab"
              onClick={() => handleNav('/app/settings')}
            >
              <span className="ql-nav-icon" aria-hidden="true">⚙️</span>
              Settings
            </button>

            {onOpenFeedback && (
              <button
                className="ql-nav-item"
                style={{ width: '100%' }}
                onClick={() => { onOpenFeedback(); close(); }}
              >
                <span className="ql-nav-icon" aria-hidden="true">💬</span>
                Send feedback
              </button>
            )}

            <button
              className="ql-nav-item"
              style={{ width: '100%', color: 'var(--text-light)' }}
              onClick={() => { logout(); close(); }}
            >
              <span className="ql-nav-icon" aria-hidden="true">↩</span>
              Log out
            </button>
          </div>
        </div>
      )}

      {/* FAB — always above sheet */}
      <button
        className={`ql-fab${isOpen ? ' ql-fab--open' : ''}`}
        onClick={() => setIsOpen((o) => !o)}
        aria-label={isOpen ? 'Close quick log' : 'Quick log'}
        aria-expanded={isOpen}
        data-testid="quick-log-fab"
      >
        +
      </button>
    </>
  );
}
